import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, ArrowRight } from 'lucide-react';

/**
 * Modal interativo para resolver taxas órfãs do iFood.
 * Exibe cada cobrança avulsa sem vínculo e permite ao usuário
 * escolher de qual lote positivo do mesmo dia o valor será descontado.
 */
const OrphanResolverModal = ({ orphans, onResolve }) => {
  if (!orphans || orphans.length === 0) return null;

  // Pegar o próximo órfão da fila (sempre resolve um de cada vez)
  const currentOrphan = orphans[0];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  const formatCurrency = (value) => {
    return 'R$ ' + Math.abs(value).toFixed(2).replace('.', ',');
  };

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent className="bg-gray-900 border-yellow-500/50 text-white max-w-lg" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-yellow-400">
            <AlertTriangle className="w-5 h-5" />
            Cobrança Avulsa do iFood
          </DialogTitle>
          <DialogDescription className="text-white/70">
            Taxa sem vínculo automático encontrada. Escolha em qual lote de repasse este valor será descontado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Informações do Órfão */}
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs text-white/60 uppercase tracking-wider">Data do Desconto</p>
                <p className="text-white font-semibold">{formatDate(currentOrphan.data)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-white/60 uppercase tracking-wider">Valor a Descontar</p>
                <p className="text-red-400 font-bold text-lg">- {formatCurrency(currentOrphan.valor)}</p>
              </div>
            </div>
            <p className="text-white/50 text-xs mt-2">{currentOrphan.descricao}</p>
          </div>

          {/* Lotes Disponíveis */}
          <div>
            <p className="text-sm text-white/80 mb-3">
              Em qual depósito deste dia esse valor foi descontado?
            </p>
            <div className="space-y-2">
              {currentOrphan.lotesDisponiveis.map((lote) => (
                <Button
                  key={lote.idSaldo}
                  variant="outline"
                  className="w-full justify-between border-white/20 text-white hover:bg-green-500/20 hover:border-green-500/50 transition-all py-6"
                  onClick={() => onResolve(currentOrphan.id, lote.idSaldo)}
                >
                  <div className="flex items-center gap-3">
                    <ArrowRight className="w-4 h-4 text-green-400" />
                    <div className="text-left">
                      <p className="font-semibold text-sm">Lote {lote.idSaldo}</p>
                      <p className="text-xs text-white/50">Valor atual: {formatCurrency(lote.valorAtual)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-green-400 font-bold text-sm">{formatCurrency(lote.valorAtual)}</p>
                    <p className="text-xs text-white/40">
                      → {formatCurrency(lote.valorAtual + currentOrphan.valor)}
                    </p>
                  </div>
                </Button>
              ))}
            </div>
          </div>

          {/* Botão Pular */}
          <Button
            variant="ghost"
            className="w-full text-white/50 hover:text-white hover:bg-white/10 mt-2"
            onClick={() => onResolve(currentOrphan.id, null)}
          >
            Pular (Ignorar Desconto)
          </Button>

          {/* Indicador de fila */}
          {orphans.length > 1 && (
            <p className="text-center text-xs text-white/40">
              {orphans.length - 1} {orphans.length - 1 === 1 ? 'cobrança restante' : 'cobranças restantes'} após esta
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrphanResolverModal;
